import { ICON__chevron_down } from '@/assets/icons_fa';
import { UI_PRIMITIVE__button } from '@/components/ui/UI_PRIMITIVE__button';
import { UI_PRIMITIVE__dropdown_menu } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu';
import { UI_PRIMITIVE__dropdown_menu_content } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_content';
import { UI_PRIMITIVE__dropdown_menu_item } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_item';
import { UI_PRIMITIVE__dropdown_menu_trigger } from '@/components/ui/dropdown/UI_PRIMITIVE__dropdown_menu_trigger';
import { use_effect } from '@/hooks/_base/use_effect';
import { use_slice_generation, use_slice_ui, use_slice_workspace } from '@/hooks/store/use_slices';
import { PLACEHOLDER_bar_line } from '@/pages/_base/views/PLACEHOLDER_bar_line';
import { UTILS__graph_data } from '@/utils/UTILS__graph_data';
import { assert_instanceof } from '@shared/asserts/utils_asserts';
import { ENUM__image_size_name, ENUM_UTILS__image_size_name } from '@shared/constants/ENUM__image_size_name';
import { DTO__image_size } from '@shared/dto/+image/image_asset_base/DTO__image_size';
import { ENUM__workspace_entity_feature } from '@shared/dto/+workspace/workspace_entity/_enums/ENUM__workspace_entity_feature';
import { FIELD_PRIMITIVE__int_image_side } from '@shared/dto/generation/graph_invoke/_fields/_primitives/number/FIELD_PRIMITIVE__int_image_side';
import { ENUM__invocation_role } from '@shared/dto/generation/graph_invoke/_invocations/_base/ENUM__invocation_role';
import { INVOCATION_GRID2D__view } from '@shared/dto/generation/graph_invoke/_invocations/grid2d/INVOCATION_GRID2D__view';
import { useMemo, useState } from 'react';

interface PROPS {
  field: FIELD_PRIMITIVE__int_image_side;
}

export function G_UI_FIELD_PRIMITIVE__int_image_side_dropdown({ field }: PROPS) {
  /*
   * STORE
   */
  const { generation_current } = use_slice_generation(['generation_current']);
  const { current_workspace_user } = use_slice_workspace(['current_workspace_user']);
  let { g_general__ui_disabled } = use_slice_ui(['g_general__ui_disabled']);

  /*
   * STATE
   */
  let { graph } = generation_current.tool;

  const aspect_ratio_grid2d_cell = useMemo(() => get_aspect_ratio_grid2d_cell(), [graph]);

  const value_current = useMemo(() => get_value_current(), [field.value]);
  const [value, set_value] = useState(field.get_value());

  const trigger_id = `G_UI_FIELD_PRIMITIVE__string_ml_asset_hash_dropdown_${field.meta.field_id}`;

  /*
   * EFFECTS
   */

  use_effect(() => set_value(field.get_value()), [field.value]);

  /*
   * EVENTS
   */
  async function on_item_selected(image_size_name: ENUM__image_size_name, value_new: number) {
    if (value_new == field.get_value()) return;

    let field_new = field.clone({ value: value_new });

    await UTILS__graph_data.update_field(graph, field_new);
  }

  /*
   *
   */

  function get_value_current() {
    return ENUM_UTILS__image_size_name.get_image_size_name(field.get_value(), aspect_ratio_grid2d_cell);
  }

  function is_disabled() {
    return g_general__ui_disabled;
  }

  function get_cursor() {
    return is_disabled() ? 'cursor-not-allowed' : 'cursor-pointer';
  }

  function get_aspect_ratio_grid2d_cell() {
    if (generation_current.is_stub()) return 0;
    if (!graph.node_role__has_node(ENUM__invocation_role.GRID2D__VIEW)) return 0;

    let node__grid2d_view = graph.node_role__get_node(ENUM__invocation_role.GRID2D__VIEW);
    assert_instanceof(node__grid2d_view, INVOCATION_GRID2D__view);

    let out = node__grid2d_view.grid2d_cell__aspect_ratio;

    return out;
  }

  /*
   *
  /

  /*
   * RENDERS
   */

  function render_item_for_size_name(image_size_name: ENUM__image_size_name) {
    if (image_size_name == ENUM__image_size_name.CUSTOM) return null;

    let longest_size = DTO__image_size.get_longest_side_for(image_size_name, aspect_ratio_grid2d_cell);

    return (
      <UI_PRIMITIVE__dropdown_menu_item
        key={`${trigger_id}_item_${image_size_name}`}
        onClick={() => on_item_selected(image_size_name, longest_size)}
        value={image_size_name}
        className={`
          w-full 
          text-left text-bar-foreground truncate
          cursor-pointer rounded-lg           
          hover:bg-accent   
          ${value_current == image_size_name ? 'bg-accent' : ''}
        `}
      >
        <div className="flex gap-2 truncate">{image_size_name}</div>
      </UI_PRIMITIVE__dropdown_menu_item>
    );
  }

  /*
   * HTML
   */

  if (field.meta.grid2d__is_dimension) return <PLACEHOLDER_bar_line />;
  if (aspect_ratio_grid2d_cell == 0) return <PLACEHOLDER_bar_line />;
  if (!current_workspace_user.is_feature_pertmitted(ENUM__workspace_entity_feature.ENTITY_GENERATION_UPDATE)) return <PLACEHOLDER_bar_line />;

  return (
    <div className="size-full p-1">
      <UI_PRIMITIVE__dropdown_menu>
        <UI_PRIMITIVE__dropdown_menu_trigger asChild>
          <UI_PRIMITIVE__button
            data-component={trigger_id}
            id={trigger_id}
            variant="outline-on-bar"
            disabled={is_disabled()}
            className={`relative flex flex-row justify-start items-center
            px-2 
            w-full h-7
            ${get_cursor()}`}
          >
            {value_current}
            <ICON__chevron_down className="size-3 absolute right-4 top-1/2 -translate-y-1/2" />
          </UI_PRIMITIVE__button>
        </UI_PRIMITIVE__dropdown_menu_trigger>
        <UI_PRIMITIVE__dropdown_menu_content {...{ trigger_id }} className="rounded-lg">
          {Object.values(ENUM__image_size_name).map(render_item_for_size_name)}
        </UI_PRIMITIVE__dropdown_menu_content>
      </UI_PRIMITIVE__dropdown_menu>
    </div>
  );
}
